/**
 * Rozbalovačka klientů (Časová osa, Import dossieru) nabízela dvě naprosto
 * shodné položky, když dva různé záznamy klienta nesly stejné jméno i e-mail:
 * administrátor pak vybíral naslepo a u importu dossieru to znamená nasypat
 * fakta k nesprávnému klientovi. Rozlišovač proto přidává SEZNAM, ne každá
 * obrazovka po svém, a přidává ho jen tam, kde je opravdu potřeba - u
 * jednoznačné položky by byl jen šum.
 */
export interface ClientPickerClient {
  id: string;
  name: string;
  email: string;
}

export interface ClientPickerOption {
  id: string;
  /** "Jméno (e-mail)" - to, co položka říká sama o sobě. */
  base: string;
  /** Pořadí mezi shodnými položkami, jinak null. */
  duplicateIndex: number | null;
}

/**
 * What makes two client rows look the same. ONE definition: the queue and the
 * client list read it through `client-identity.ts` (F138 vada 11), so a second
 * copy cannot decide differently about the same pair of records.
 */
export function clientIdentityKey(name: string, email: string): string {
  return `${name.trim().toLowerCase()}|${email.trim().toLowerCase()}`;
}

function identity(client: ClientPickerClient): string {
  return clientIdentityKey(client.name, client.email);
}

/**
 * Pořadové číslo mezi položkami, které na obrazovce vypadají stejně, nebo
 * null u položky, která je jediná svého druhu.
 *
 * Rozlišovač nepatří rozbalovačce, patří KAŽDÉMU seznamu, kde se dva různé
 * záznamy vykreslí do nerozeznatelných řádků. V Historii importů stálo šest
 * takových dvojic (`019fb71f-…` a `019fd620-…` se shodným jménem, e-mailem,
 * stavem, časem i poměrem faktů) a "Otevřít klienta" u nich vedlo ke dvěma
 * různým klientům, aniž by šlo poznat ke kterému (audit S2-20). Klíč si
 * dodává volající: je to přesně to, co je v řádku VIDĚT.
 */
export function duplicateIndexes(keys: readonly string[]): Array<number | null> {
  const totals = new Map<string, number>();
  for (const key of keys) totals.set(key, (totals.get(key) ?? 0) + 1);
  const seen = new Map<string, number>();
  return keys.map((key) => {
    const index = (seen.get(key) ?? 0) + 1;
    seen.set(key, index);
    return (totals.get(key) ?? 0) > 1 ? index : null;
  });
}

export function clientPickerOptions(
  clients: readonly ClientPickerClient[]
): ClientPickerOption[] {
  const indexes = duplicateIndexes(clients.map(identity));
  return clients.map((client, position) => ({
    id: client.id,
    base: `${client.name} (${client.email})`,
    duplicateIndex: indexes[position]
  }));
}

/** Jedno složení popisku pro obě rozbalovačky. */
export function clientPickerLabel(
  option: ClientPickerOption,
  t: (key: string) => string
): string {
  if (option.duplicateIndex === null) return option.base;
  return `${option.base} · ${t("staffConsole.clientPickerDuplicate").replace("{index}", String(option.duplicateIndex))}`;
}
